import type { LifecycleStage, Recency } from './types';

export const fmtNumber = (value: number | null | undefined): string =>
    value == null ? '-' : value.toLocaleString('en-US');

export const fmtPct = (rate: number | null | undefined, digits = 0): string =>
    rate == null ? '-' : `${(rate * 100).toFixed(digits)}%`;

/** Signed change, e.g. "+12%" or "-5%". */
export const fmtDelta = (rate: number | null | undefined): string =>
    rate == null ? '-' : `${rate > 0 ? '+' : ''}${Math.round(rate * 100)}%`;

/** A duration given in hours, in the unit a person would say it in. */
export const fmtHours = (hours: number | null | undefined): string => {
    if (hours == null) return '-';
    if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
    if (hours < 48) return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)}h`;
    const days = hours / 24;
    return `${days < 10 ? days.toFixed(1) : Math.round(days)}d`;
};

export const fmtMinutes = (minutes: number | null | undefined): string =>
    minutes == null ? '-' : fmtHours(minutes / 60);

export const fmtDate = (iso: string | null | undefined): string => {
    if (!iso) return '-';
    const date = new Date(iso);
    return isNaN(date.getTime()) ? iso : date.toISOString().slice(0, 10);
};

export const fmtDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '-';
    const date = new Date(iso);
    return isNaN(date.getTime()) ? iso : `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};

export const fmtDaysAgo = (days: number | null | undefined): string => {
    if (days == null) return 'never';
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    return `${days}d ago`;
};

export const CHANNEL_LABELS: Record<string, string> = {
    unknown: 'Unknown (before tracking)',
    direct: 'Direct',
    landing: 'Landing site',
    blog: 'Blog',
    search: 'Search',
    ai: 'AI assistants',
    social: 'Social',
    email: 'Email',
    campaign: 'Campaign (UTM)',
    referral: 'Other sites',
};

export const STAGE_LABELS: Record<LifecycleStage, string> = {
    unconfirmed: 'Unconfirmed',
    signed_up: 'Signed up only',
    exploring: 'Exploring',
    activated: 'Activated',
    committed: 'Committed',
};

export const STAGE_HINTS: Record<LifecycleStage, string> = {
    unconfirmed: 'Never clicked the confirmation link',
    signed_up: 'Confirmed, but never opened a stage or a challenge',
    exploring: 'Has opened content, no solve yet',
    activated: '1-9 solves (theory stages and challenges)',
    committed: '10+ solves',
};

export const RECENCY_LABELS: Record<Recency, string> = {
    active: 'Active ≤7d',
    cooling: 'Cooling 8-30d',
    dormant: 'Dormant 31-90d',
    churned: 'Churned 90d+',
    never: 'No activity',
};

export const LEARNING_LABELS: Record<string, string> = {
    theory: 'Theory first',
    challenge: 'Challenge first',
    quiz: 'Tech quiz first',
    theory_only: 'Theory only',
    challenges_only: 'Challenges only',
    both: 'Theory + challenges',
    quiz_only: 'Tech quizzes only',
    none: 'Nothing yet',
};

export const TYPE_LABELS: Record<string, string> = {
    academy: 'Academy',
    mission: 'Mission',
    protocol: 'Protocol',
};
